'use client'
import type { ComponentType, ErrorInfo } from 'react'
import React, { Component, useEffect } from 'react'
import { ErrorBoundaryContext } from './ErrorBoundaryContext'
import { onError, onFeatureInit } from './event-handlers'
import type { ErrorBoundaryProps, ErrorBoundaryState, Tier, WithErrorBoundaryProps } from './types'

const UNKNOWN_VALUE = '__UNKNOWN__'

const InitFeature = ({ id, owner, customContext, tier }: WithErrorBoundaryProps) => {
  // eslint-disable-next-line o11y-guard/error-tracking
  useEffect(() => {
    if (onFeatureInit) {
      // Old flavor of the metric  (can be deleted when dashboards are updated with new metric)
      onFeatureInit(`${id}.init`, {
        owner,
        errorBoundary: id,
        customContext: customContext || {},
        tier: tier || UNKNOWN_VALUE,
      })
      // New flavor of the metric
      onFeatureInit('error_boundary.init', {
        owner,
        errorBoundary: id,
        customContext: customContext || {},
        tier: tier || UNKNOWN_VALUE,
      })
    }
  }, [id, owner, customContext, tier])

  return <></>
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)

    this.state = { hasError: false }
  }

  static getDerivedStateFromError = (): ErrorBoundaryState => ({ hasError: true })

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    if (!onError) {
      return
    }
    onError(error, {
      errorInfo,
      errorBoundary: this.props.id,
      customContext: this.props.customContext || {},
      owner: this.props.owner,
      tier: this.props?.tier || (UNKNOWN_VALUE as unknown as Tier),
    })
  }

  render() {
    const { owner, id, customContext, fallback, children } = this.props

    return (
      <ErrorBoundaryContext.Provider value={{ owner, id, customContext }}>
        <InitFeature {...this.props} />
        {this.state.hasError && fallback ? fallback : children}
      </ErrorBoundaryContext.Provider>
    )
  }
}

function withErrorBoundary<Props extends object>(
  WrappedComponent: ComponentType<Props>,
  { fallback, owner, customContext, id }: WithErrorBoundaryProps
): React.FC<Props> {
  const ComponentWithErrorBoundary: React.FC<Props> = (props: Props) => (
    <ErrorBoundary customContext={customContext} fallback={fallback} id={id} owner={owner}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  )

  ComponentWithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`

  return ComponentWithErrorBoundary
}

export { withErrorBoundary }
